import { useCallback, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  ActionDependant,
  evaluateTemplateValue,
  GlobalDataContext,
  TemplateContext,
} from '@ea-lab/reactive-json';
import { saveDocPage, deleteDocPage, archiveDocPage, unarchiveEntity } from '../../../services/api';
import {
  Pencil,
  Trash2,
  ChevronDown,
  Send,
  Check,
  RotateCcw,
  Archive,
  MoreHorizontal,
  Settings,
  User,
  Clock,
  Tag,
  X,
  ArrowRight,
  ArrowLeft,
  Eye,
  EyeOff,
} from 'lucide-react';
import ConfirmTrashModal from '../../shared/ui/ConfirmTrashModal';
import { DirectorySettingsBody } from '../settings/DocDirectorySettings';
import { useDocArchived } from '../DocArchivedContext';
import { useAuth } from '../../../context/AuthContext';

const WORKFLOW_TRANSITIONS = {
  draft: {
    forward: [{ label: 'Soumettre en relecture', target: 'in_review', icon: Send }],
    backward: [],
  },
  in_review: {
    forward: [{ label: 'Publier', target: 'published', icon: Check }],
    backward: [{ label: 'Repasser en brouillon', target: 'draft', icon: RotateCcw }],
  },
  published: {
    forward: [{ label: 'Marquer obsolète', target: 'deprecated', icon: Archive }],
    backward: [
      { label: 'Repasser en relecture', target: 'in_review', icon: RotateCcw },
      { label: 'Repasser en brouillon', target: 'draft', icon: RotateCcw },
    ],
  },
  deprecated: {
    forward: [],
    backward: [{ label: 'Réactiver (brouillon)', target: 'draft', icon: RotateCcw }],
  },
};

const STATE_LABELS = {
  draft: 'Brouillon',
  in_review: 'En relecture',
  published: 'Publié',
  deprecated: 'Obsolète',
};

const STATE_COLORS = {
  draft: 'bg-stone-100 text-stone-600',
  in_review: 'bg-amber-100 text-amber-700',
  published: 'bg-emerald-100 text-emerald-700',
  deprecated: 'bg-red-100 text-red-700',
};

function formatDate(isoStr) {
  if (!isoStr) return '';
  try {
    return new Date(isoStr).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return isoStr; }
}

export const DocPageActions = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const navigate = useNavigate();
  const archivedAt = useDocArchived();
  const { isAdmin } = useAuth();
  const [workflowOpen, setWorkflowOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [archivingPage, setArchivingPage] = useState(false);
  const [showUnarchiveConfirm, setShowUnarchiveConfirm] = useState(false);
  const [unarchiving, setUnarchiving] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const pageId = evaluateTemplateValue({
    valueToEvaluate: props.pageId,
    globalDataContext,
    templateContext,
  });


  const pageTitle = evaluateTemplateValue({
    valueToEvaluate: props.pageTitle,
    globalDataContext,
    templateContext,
  });

  const directoryId = evaluateTemplateValue({
    valueToEvaluate: props.directoryId,
    globalDataContext,
    templateContext,
  });

  const pageIcon = evaluateTemplateValue({
    valueToEvaluate: props.pageIcon,
    globalDataContext,
    templateContext,
  }) || '';

  const moderationState = evaluateTemplateValue({
    valueToEvaluate: props.moderationState,
    globalDataContext,
    templateContext,
  });

  const editMode = evaluateTemplateValue({
    valueToEvaluate: props.editMode,
    globalDataContext,
    templateContext,
  });

  const author = evaluateTemplateValue({
    valueToEvaluate: props.author,
    globalDataContext,
    templateContext,
  });

  const category = evaluateTemplateValue({
    valueToEvaluate: props.category,
    globalDataContext,
    templateContext,
  });

  const updatedAt = evaluateTemplateValue({
    valueToEvaluate: props.updatedAt,
    globalDataContext,
    templateContext,
  });

  const editModePath = props.editModePath || 'editMode';

  const transitionGroup = WORKFLOW_TRANSITIONS[moderationState] || { forward: [], backward: [] };
  const hasTransitions = transitionGroup.forward.length > 0 || transitionGroup.backward.length > 0;

  useEffect(() => {
    if (!workflowOpen && !moreOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') { setWorkflowOpen(false); setMoreOpen(false); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [workflowOpen, moreOpen]);

  const handleToggleEdit = useCallback(() => {
    globalDataContext.updateData(!editMode, editModePath);
  }, [globalDataContext, editMode, editModePath]);

  const handleTransition = useCallback(async (targetState) => {
    if (!pageId) return;
    setSaving(true);
    try {
      await saveDocPage({ id: pageId, moderationState: targetState });
      window.location.reload();
    } catch (err) {
      console.error('Workflow transition failed:', err);
    } finally {
      setSaving(false);
      setWorkflowOpen(false);
    }
  }, [pageId]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!pageId || deleting) return;
    setDeleting(true);
    try {
      await deleteDocPage(pageId);
      setShowConfirm(false);
      const dirId = directoryId ? parseInt(directoryId, 10) : null;
      window.dispatchEvent(new CustomEvent('doc:entity-trashed', {
        detail: { type: 'page', id: pageId, parentId: dirId },
      }));
      navigate('/docs');
    } catch (err) {
      console.error('Trash failed:', err);
    } finally {
      setDeleting(false);
    }
  }, [pageId, directoryId, deleting, navigate]);

  const handleArchiveConfirm = useCallback(async () => {
    if (!pageId || archivingPage) return;
    setArchivingPage(true);
    try {
      await archiveDocPage(pageId);
      setShowArchiveConfirm(false);
      const dirId = directoryId ? parseInt(directoryId, 10) : null;
      window.dispatchEvent(new CustomEvent('doc:entity-archived', {
        detail: { type: 'page', id: pageId, parentId: dirId },
      }));
      navigate('/docs');
    } catch (err) {
      console.error('Archive failed:', err);
      const msg = err.message?.includes('403')
        ? 'Permission refusée. Vous n\'avez pas les droits pour archiver cette page.'
        : `Erreur : ${err.message}`;
      alert(msg);
    } finally {
      setArchivingPage(false);
    }
  }, [pageId, directoryId, archivingPage, navigate]);

  const handleUnarchiveConfirm = useCallback(async () => {
    if (!pageId || unarchiving) return;
    setUnarchiving(true);
    try {
      await unarchiveEntity('node', 'builder_page', pageId);
      setShowUnarchiveConfirm(false);
      window.location.reload();
    } catch (err) {
      console.error('Unarchive failed:', err);
      alert(`Erreur : ${err.message}`);
    } finally {
      setUnarchiving(false);
    }
  }, [pageId, unarchiving]);

  const title = pageTitle || `Page #${pageId}`;
  const isArchived = !!archivedAt;

  const stateLabel = STATE_LABELS[moderationState] || moderationState || 'Statut';

  return (
    <ActionDependant {...props}>
      <div className="flex items-center flex-1">
        {/* ── Left group: action buttons ── */}
        <div className="flex items-center gap-1.5">
          {/* Edit toggle */}
          {!isArchived && (
            <button
              onClick={handleToggleEdit}
              className={`
                inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-150
                ${editMode
                  ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
                  : 'border border-stone-200 text-stone-700 hover:bg-stone-50'}
              `}
            >
              {editMode ? <><X size={14} /> Annuler</> : <><Pencil size={14} /> Modifier</>}
            </button>
          )}

          {/* Workflow dropdown — hidden in edit mode */}
          {!editMode && !isArchived && hasTransitions && (
            <div className="relative">
              <button
                onClick={() => setWorkflowOpen(!workflowOpen)}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-blue-200 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50 transition-all disabled:opacity-50"
              >
                {saving ? 'En cours...' : stateLabel}
                <ChevronDown size={14} className={`transition-transform ${workflowOpen ? 'rotate-180' : ''}`} />
              </button>
              {workflowOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setWorkflowOpen(false)} />
                  <div className="absolute left-0 mt-2 w-60 bg-white rounded-xl shadow-lg border border-stone-200 py-1 z-50">
                    {transitionGroup.forward.length > 0 && (
                      <>
                        <div className="px-3 pt-2 pb-1">
                          <span className="text-[10px] uppercase tracking-widest font-semibold text-stone-400">Avancer</span>
                        </div>
                        {transitionGroup.forward.map((t) => (
                          <button
                            key={t.target}
                            onClick={() => handleTransition(t.target)}
                            className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-stone-700 hover:bg-blue-50 transition-colors text-left"
                          >
                            <ArrowRight size={14} className="text-blue-500 flex-shrink-0" />
                            {t.label}
                          </button>
                        ))}
                      </>
                    )}
                    {transitionGroup.forward.length > 0 && transitionGroup.backward.length > 0 && (
                      <hr className="my-1 border-stone-100" />
                    )}
                    {transitionGroup.backward.length > 0 && (
                      <>
                        <div className="px-3 pt-2 pb-1">
                          <span className="text-[10px] uppercase tracking-widest font-semibold text-stone-400">Revenir</span>
                        </div>
                        {transitionGroup.backward.map((t) => (
                          <button
                            key={t.target}
                            onClick={() => handleTransition(t.target)}
                            className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-stone-500 hover:bg-stone-50 transition-colors text-left"
                          >
                            <ArrowLeft size={14} className="text-stone-400 flex-shrink-0" />
                            {t.label}
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Unarchive (visible only when archived + admin) */}
          {isArchived && isAdmin && (
            <button
              onClick={() => setShowUnarchiveConfirm(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-green-200 text-green-600 hover:bg-green-50 rounded-lg text-sm font-medium transition-all"
              aria-label="Désarchiver"
            >
              <RotateCcw size={14} />
              Désarchiver
            </button>
          )}

          {/* Aucun bouton « Enregistrer » ici : en édition, c'est LE BUILDER
              qui est monté, et c'est SA barre qui enregistre — le contenu et le
              titre partent ensemble, en un seul geste. Un second bouton de
              sauvegarde à côté n'aurait rien à sauver de plus, et laissait
              croire à deux enregistrements distincts. */}
        </div>

        {/* ── Right group: utilities, always visible ── */}
        <div className="flex items-center gap-0.5 ml-auto">
          <div className="w-px h-5 bg-stone-200 mx-2" aria-hidden="true" />
          <button
            onClick={() => setShowSettings(true)}
            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors"
            aria-label="Paramètres de la page"
          >
            <Settings size={16} />
          </button>
          <div className="relative">
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors"
              aria-label="Plus d'actions"
              aria-expanded={moreOpen}
            >
              <MoreHorizontal size={16} />
            </button>
            {moreOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMoreOpen(false)} />
                <div className="absolute right-0 mt-1 w-64 bg-white rounded-xl shadow-lg border border-stone-200 z-50 overflow-hidden">
                  <div className="px-4 pt-3 pb-2">
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-stone-400">Informations</span>
                  </div>
                  <div className="px-4 pb-3 space-y-2">
                    {author && (
                      <div className="flex items-center gap-2.5 text-xs text-stone-600">
                        <User size={13} className="text-stone-400 flex-shrink-0" />
                        <span className="truncate">{author}</span>
                      </div>
                    )}
                    {updatedAt && (
                      <div className="flex items-center gap-2.5 text-xs text-stone-600">
                        <Clock size={13} className="text-stone-400 flex-shrink-0" />
                        <span className="truncate">{formatDate(updatedAt)}</span>
                      </div>
                    )}
                    {category && (
                      <div className="flex items-center gap-2.5 text-xs text-stone-600">
                        <Tag size={13} className="text-stone-400 flex-shrink-0" />
                        <span className="truncate">{category}</span>
                      </div>
                    )}
                    {moderationState && (
                      <div className="flex items-center gap-2.5 pt-0.5">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-2xs font-semibold ${STATE_COLORS[moderationState] || STATE_COLORS.draft}`}>
                          {STATE_LABELS[moderationState] || moderationState}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="border-t border-stone-100 py-1">
                    {!isArchived && (
                      <button
                        onClick={() => { setMoreOpen(false); setShowArchiveConfirm(true); }}
                        className="flex items-center gap-2.5 w-full px-4 py-2 text-sm text-stone-600 hover:bg-stone-50 transition-colors text-left"
                      >
                        <Archive size={14} className="text-stone-400" />
                        Archiver
                      </button>
                    )}
                    <button
                      onClick={() => { setMoreOpen(false); setShowConfirm(true); }}
                      className="flex items-center gap-2.5 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors text-left"
                    >
                      <Trash2 size={14} />
                      Supprimer
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {showSettings && createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setShowSettings(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl border border-stone-200 max-h-[85vh] flex flex-col">
            <DirectorySettingsBody
              itemId={pageId}
              itemType="page"
              currentName={pageTitle}
              currentIcon={pageIcon}
              currentParentId={directoryId}
              onClose={() => setShowSettings(false)}
              onSaved={() => window.location.reload()}
            />
          </div>
        </div>,
        document.body,
      )}

      <ConfirmTrashModal
        isOpen={showConfirm}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowConfirm(false)}
        title={`Supprimer la page « ${title} » ?`}
        description="Cette page sera déplacée dans la corbeille. Vous pourrez la restaurer pendant 90 jours."
        confirmLabel="Déplacer dans la corbeille"
        loading={deleting}
      />

      {!isArchived && (
        <ConfirmTrashModal
          isOpen={showArchiveConfirm}
          onConfirm={handleArchiveConfirm}
          onCancel={() => setShowArchiveConfirm(false)}
          title={`Archiver la page « ${title} » ?`}
          description="Cette page sera archivée en lecture seule. Vous pourrez la désarchiver à tout moment."
          confirmLabel="Archiver"
          loading={archivingPage}
          variant="archive"
        />
      )}

      {isArchived && (
        <ConfirmTrashModal
          isOpen={showUnarchiveConfirm}
          onConfirm={handleUnarchiveConfirm}
          onCancel={() => setShowUnarchiveConfirm(false)}
          title={`Désarchiver la page « ${title} » ?`}
          description="Cette page redeviendra active et modifiable."
          confirmLabel="Désarchiver"
          loading={unarchiving}
        />
      )}
    </ActionDependant>
  );
};
